/**
 * Per-app "last opened" history, backing `mash_opened`'s `daysSinceLastOpen`
 * property and the `mash_returned_to` event.
 *
 * There is no server-side `last_opened_at` column, so this is client-local:
 * one `localStorage` entry mapping appId → epoch ms of the previous open.
 * Consequences worth knowing when reading the dashboards:
 *
 *   - A user on a new device looks like a first-time opener of every app.
 *   - Clearing site data resets every app to "never opened".
 *
 * Both skew `mash_returned_to` down, never up, so the retention curve is a
 * floor rather than an inflated number. Promote to a backend column if that
 * floor gets too soft.
 *
 * `localStorage`, not `@capacitor/preferences` — the latter's native calls
 * hang in the Android WebView.
 */

export const APP_OPENS_KEY = "mash:app_opens";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface AppOpenResult {
  /** True when this app has been opened on this device before. */
  isReturn: boolean;
  /** Whole days since the previous open; `null` on a first open. */
  daysSinceLastOpen: number | null;
}

type OpenMap = Record<string, number>;

function readMap(): OpenMap {
  try {
    const raw = localStorage.getItem(APP_OPENS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    // Drop anything that isn't a finite timestamp — a hand-edited or
    // half-written entry must not produce a NaN day count downstream.
    const out: OpenMap = {};
    for (const [key, value] of Object.entries(
      parsed as Record<string, unknown>,
    )) {
      if (typeof value === "number" && Number.isFinite(value)) out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

function writeMap(map: OpenMap): void {
  try {
    localStorage.setItem(APP_OPENS_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable or full — analytics must not break the runtime */
  }
}

/**
 * Record that `appId` was just opened, and report what the *previous* state
 * was. Call exactly once per app open — it mutates the stored timestamp.
 */
export function recordAppOpen(
  appId: string,
  now: number = Date.now(),
): AppOpenResult {
  const map = readMap();
  const previous = map[appId];

  const result: AppOpenResult =
    previous === undefined
      ? { isReturn: false, daysSinceLastOpen: null }
      : {
          isReturn: true,
          // Clamped at 0: a clock that moved backwards should read as
          // "today", not as a negative retention bucket.
          daysSinceLastOpen: Math.max(
            0,
            Math.floor((now - previous) / MS_PER_DAY),
          ),
        };

  map[appId] = now;
  writeMap(map);
  return result;
}

/**
 * Drop the open history on sign-out. The map is device-scoped but describes
 * one user's behaviour: without this, the next account to sign in would look
 * like it was "returning to" apps it has never opened.
 */
export function clearAppOpenHistory(): void {
  try {
    localStorage.removeItem(APP_OPENS_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
